import axios, { AxiosInstance } from 'axios';
import { getAccessToken, onUnauthorized } from './auth';

/**
 * Builds an axios instance authenticated with the current access token.
 *
 * - If there is no token, triggers the global `onUnauthorized` handler
 *   (clears the session and redirects to login) and throws.
 * - Installs a response interceptor that routes any server-side 401 to
 *   `onUnauthorized`, so an expired/invalid token logs the user out and
 *   redirects — matching the behaviour of the generated fetch clients.
 *
 * Shared by all the hand-written axios API modules (bookings, customers,
 * services, colaborators, import-customers).
 */
export function createAuthenticatedApi(baseURL: string): AxiosInstance {
  const accessToken = getAccessToken();
  if (!accessToken) {
    onUnauthorized();
    throw new Error('Sessão expirada');
  }

  const instance = axios.create({
    baseURL,
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        onUnauthorized();
      }
      return Promise.reject(error);
    },
  );

  return instance;
}
